/**
 * Custom Map Marker Icons for Ops Dashboard
 * 
 * This module provides SVG-based marker icons for the Google Maps integration.
 * - Driver markers: Car icons with status-based colors
 * - Booking markers: Pickup pin icons
 * 
 * All icons are optimized SVG paths for performance and scalability.
 */

/**
 * Generate a car icon for driver markers
 * @param {string} status - Driver status: 'active', 'available', 'inactive', etc.
 * @returns {object} Google Maps marker icon configuration
 */
export const getDriverIcon = (status = 'available', tripStatus = null, zoom = 15, isTransporter = false) => {
    // Color mapping based on driver status
    const colorMap = {
        active: '#3B82F6',      // Blue - on trip (flowing, no action needed)
        available: '#10B981',   // Green - available (idle, ready for assignment)
        inactive: '#F59E0B',    // Orange - inactive/offline (at base)
        default: '#9CA3AF'      // Light gray - unknown status
    };

    let fillColor = colorMap[status] || colorMap.default;

    // Override color for transporter vehicles -> Light Pink
    if (isTransporter) {
        fillColor = '#f472b6';
    } else if (tripStatus === 'accepted') {
        fillColor = '#FFFFFF';
    }

    // Dynamic scale based on zoom level to prevent overlaps when zoomed out
    const baseScale = Math.max(0.5, Math.min(1.4, 0.5 + (zoom - 10) * 0.15));

    // Modern car icon SVG path (top-down view)
    return {
        // Truck SVG Path
        path: 'M20 8h-3V4H3c-1.1 0-2 .9-2 2v11h2c0 1.66 1.34 3 3 3s3-1.34 3-3h6c0 1.66 1.34 3 3 3s3-1.34 3-3h2v-5l-3-4zM6 18c-.55 0-1-.45-1-1s.45-1 1-1 1 .45 1 1-.45 1-1 1zm13.5-8.5l1.96 2.5H17V9.5h2.5zM18 18c-.55 0-1-.45-1-1s.45-1 1-1 1 .45 1 1-.45 1-1 1z',
        fillColor: fillColor,
        fillOpacity: 0.88,
        strokeWeight: 1.25,
        strokeColor: '#334155',
        scale: baseScale,
        // Anchor at bottom of truck so map position = vehicle on road; InfoWindow sits above
        anchor: { x: 12, y: 20 },
        labelOrigin: { x: 12, y: 10 }
    };
};

/**
 * Generate a pickup pin icon for booking markers
 * @param {string} status - Booking status: 'Pending', 'Assigned', etc.
 * @param {number} zoom - Current map zoom level
 * @returns {object} Google Maps marker icon configuration
 */
export const getBookingIcon = (status = 'Pending', zoom = 15) => {
    // Color mapping based on booking status
    const colorMap = {
        Pending: '#FFFFFF',      // White - new booking, not yet in 15-min cycle
        Unassigned: '#EF4444',   // Red    - cycle ran, no driver found yet
        Delayed: '#A855F7',      // Purple - assigned but pickup time has passed
        Overdue: '#A855F7',      // Purple - pending but pickup time has passed
        Assigned: '#3B82F6',     // Blue   - successfully assigned
        Completed: '#10B981',    // Green  - booking completed
        default: '#3B82F6'
    };

    const fillColor = colorMap[status] || colorMap.default;

    // Dynamic scale based on zoom level to prevent overlaps when zoomed out
    const baseScale = Math.max(0.5, Math.min(1.4, 0.5 + (zoom - 10) * 0.15));

    // Classic map pin shape
    return {
        path: 'M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z',
        fillColor: fillColor,      // Dynamic color based on status
        fillOpacity: 1,
        strokeWeight: 2,
        strokeColor: '#1a1a1a',
        scale: baseScale,
        anchor: { x: 12, y: 22 },  // Anchor at pin bottom
        labelOrigin: { x: 12, y: 9 }
    };
};

/**
 * Generate a drop-point pin icon (shown when a booking is selected)
 * Same pin shape as booking icon, distinct color so drop is clearly different from pickup.
 * @param {number} zoom - Current map zoom level
 * @returns {object} Google Maps marker icon configuration
 */
export const getDropIcon = (zoom = 15) => {
    // Dynamic scale based on zoom level to prevent overlaps when zoomed out
    const baseScale = Math.max(0.5, Math.min(1.4, 0.5 + (zoom - 10) * 0.15));

    return {
        // Pushpin SVG Path
        path: 'M16 9V4.5C16 3.12 14.88 2 13.5 2h-3C9.12 2 8 3.12 8 4.5V9c-2.21 0-4 1.79-4 4s1.79 4 4 4h3v4h2v-4h3c2.21 0 4-1.79 4-4s-1.79-4-4-4z',
        fillColor: '#EF4444', // Red pushpin
        fillOpacity: 1,
        strokeWeight: 1.5,
        strokeColor: '#1a1a1a',
        scale: baseScale,
        anchor: { x: 12, y: 21 }, // Anchor at pin bottom
        labelOrigin: { x: 12, y: 9 }
    };
};

/**
 * Hover effect configuration for markers
 * Can be applied on mouse over/out events
 */
export const markerHoverStyle = {
    scale: 1.6,  // Slightly larger on hover
    zIndex: 1000 // Bring to front
};

/**
 * Default marker style (non-hover)
 */
export const markerDefaultStyle = {
    scale: 1.4,  // Driver default
    zIndex: 1
};

export const bookingMarkerDefaultStyle = {
    scale: 1.2,  // Booking default
    zIndex: 1
};

/**
 * Generate a pin icon for pincode coverage centers
 * @returns {object} Google Maps marker icon configuration
 */
export const getPincodeIcon = () => {
    return {
        path: 'M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z',
        fillColor: '#3B82F6', // Blue pin for pincode focus
        fillOpacity: 1,
        strokeWeight: 2,
        strokeColor: '#ffffff',
        scale: 1.4,
        anchor: { x: 12, y: 22 }
    };
};

const createTriangleSvg = () => {
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 40 40" width="40" height="40">
        <defs>
            <filter id="shadow" x="-20%" y="-20%" width="140%" height="140%">
                <feDropShadow dx="0" dy="2" stdDeviation="1.5" flood-color="#000" flood-opacity="0.35"/>
            </filter>
            <linearGradient id="redGrad" x1="0%" y1="0%" x2="0%" y2="100%">
                <stop offset="0%" stop-color="#EF4444"/>
                <stop offset="100%" stop-color="#B91C1C"/>
            </linearGradient>
            <style>
                @keyframes heartbeat {
                    0% { transform: scale(1); }
                    14% { transform: scale(1.15); }
                    28% { transform: scale(1); }
                    42% { transform: scale(1.15); }
                    70% { transform: scale(1); }
                }
                .pulse-group {
                    animation: heartbeat 1.5s infinite ease-in-out;
                    transform-origin: 20px 32px;
                }
            </style>
        </defs>
        <g class="pulse-group">
            <path d="M 20,32 C 19.5,32 19.1,31.7 18.8,31.3 L 10.8,17.3 C 10.3,16.5 10.7,15 11.8,15 L 28.2,15 C 29.3,15 29.7,16.5 29.2,17.3 L 21.2,31.3 C 20.9,31.7 20.5,32 20,32 Z" 
                  fill="url(#redGrad)" 
                  stroke="#FFFFFF" 
                  stroke-width="2" 
                  filter="url(#shadow)"
            />
            <path d="M 20,18.5 L 20,22.5" 
                  stroke="#FFFFFF" 
                  stroke-width="2" 
                  stroke-linecap="round"
            />
            <circle cx="20" cy="25.5" r="1.2" fill="#FFFFFF"/>
        </g>
    </svg>`;
    return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
};

/**
 * Generate a red triangle marker icon for city-wide pincodes
 * @param {number} zoom - Current map zoom level to dynamically scale the marker
 * @returns {object} Google Maps marker icon configuration
 */
export const getPincodeTriangleIcon = (zoom = 12) => {
    if (typeof window.google === 'undefined') return null;

    // Scale size dynamically based on zoom level: larger when zooming in, slightly smaller when zooming out
    const size = Math.max(24, Math.min(60, 32 + (zoom - 12) * 4.5));
    const scale = size / 32;

    return {
        url: createTriangleSvg(),
        scaledSize: new window.google.maps.Size(size, size),
        anchor: new window.google.maps.Point(16 * scale, 26 * scale)
    };
};
